import { apiFetch } from './api';

// Part is one rendered piece of a stored message: either an assistant/user text
// run or a tool invocation. Mirrors the server's agent.Part (input is raw JSON
// the client summarizes for display).
export interface Part {
  kind: 'text' | 'tool';
  text?: string;
  name?: string;
  input?: unknown;
}

// Message is one stored turn entry returned by getAgent()'s history.
export interface Message {
  role: 'user' | 'assistant';
  parts: Part[];
  ts: string;
}

// AgentInfo is a row in the agent list (GET /api/agents).
export interface AgentInfo {
  id: string;
  cwd: string;
  title: string;
  // User-settable name. Auto-derived server-side from the first prompt; PATCH
  // /api/agents/{id} overrides it. `title` mirrors the effective name.
  name?: string;
  busy: boolean;
  updatedAt: string;
}

// AgentDetail is the full conversation returned by GET /api/agents/{id}.
export interface AgentDetail {
  id: string;
  cwd: string;
  busy: boolean;
  messages: Message[];
}

// createAgent starts a new agent session in the given working directory. cwd
// defaults server-side (~) when omitted; model/permissionMode are optional.
export async function createAgent(cwd?: string, model?: string, permissionMode?: string): Promise<string> {
  const body: Record<string, string> = {};
  if (cwd) body.cwd = cwd;
  if (model) body.model = model;
  if (permissionMode) body.permissionMode = permissionMode;
  const r = await apiFetch('/api/agents', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await r.json()).id;
}

export async function listAgents(): Promise<AgentInfo[]> {
  const r = await apiFetch('/api/agents');
  return r.json();
}

export async function getAgent(id: string): Promise<AgentDetail> {
  const r = await apiFetch(`/api/agents/${id}`);
  return r.json();
}

// sendMessage posts a prompt; the turn runs asynchronously and its events stream
// over the agent WebSocket, so this resolves as soon as the turn is accepted.
export async function sendMessage(id: string, text: string): Promise<void> {
  await apiFetch(`/api/agents/${id}/messages`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ text }),
  });
}

export async function deleteAgent(id: string): Promise<void> {
  await apiFetch(`/api/agents/${id}`, { method: 'DELETE' });
}

// renameAgent sets a chat's display name. PATCH overrides server auto-naming and
// is never overwritten later; the caller updates its row optimistically.
export async function renameAgent(id: string, name: string): Promise<void> {
  await apiFetch(`/api/agents/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name }),
  });
}
